import { useEffect, useMemo, useState } from "react";
import { Sparkles, Loader2, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { generateAiMoodboard } from "@/api";
import { isPdf, categoryLabel } from "@/lib/utils";

const MOODS = [
  { key: "Minimal", swatch: ["#F2F1ED", "#1c1b17"], blurb: "calm, modern restraint" },
  { key: "Cozy", swatch: ["#E8E0D2", "#3d2b1f"], blurb: "warm, layered, tactile" },
  { key: "Dramatic", swatch: ["#1C1B17", "#F2EBDC"], blurb: "moody, high-contrast" },
  { key: "Coastal", swatch: ["#E6EEF2", "#22404e"], blurb: "airy, breezy, sand & sea" },
  { key: "Industrial", swatch: ["#2A2E2A", "#E6E2D7"], blurb: "raw concrete & metal" },
  { key: "Scandinavian", swatch: ["#F7F5F0", "#2a2a28"], blurb: "bright wood & white" },
  { key: "Bohemian", swatch: ["#E8D9C2", "#5a3a1f"], blurb: "eclectic, earthy textiles" },
  { key: "Mid Century", swatch: ["#E2C58E", "#3c2510"], blurb: "walnut & brass" },
  { key: "Japandi", swatch: ["#EFE9DF", "#3a352c"], blurb: "Japanese × Scandinavian" },
  { key: "Luxe", swatch: ["#1F1A17", "#D7B36C"], blurb: "velvet, marble, gold" },
  { key: "Biophilic", swatch: ["#E5EADF", "#2b3a26"], blurb: "greenery, organic light" },
  { key: "Rustic", swatch: ["#D8CAB0", "#3a2812"], blurb: "aged wood & stone" },
];

export default function AiMoodboardDialog({
  open,
  onOpenChange,
  projectId,
  images,
  onCreated,
  trigger,
}) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [mood, setMood] = useState("Minimal");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setName("");
      setMood("Minimal");
      setSelected(new Set());
    }
  }, [open]);

  const selectable = useMemo(
    () => images.filter((i) => !!i.data),
    [images]
  );

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(selectable.map((i) => i.id)));
  }
  function clearSelection() {
    setSelected(new Set());
  }

  async function handleGenerate() {
    if (!name.trim()) {
      toast.error("Give the moodboard a name");
      setStep(1);
      return;
    }
    if (selected.size === 0) {
      toast.error("Pick at least one image or file");
      setStep(2);
      return;
    }
    setBusy(true);
    try {
      // Preserve user selection order (insertion order in Set)
      const ids = Array.from(selected);
      const mb = await generateAiMoodboard({
        project_id: projectId,
        name: name.trim(),
        mood,
        image_ids: ids,
      });
      toast.success("Moodboard generated");
      onCreated?.(mb);
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Generation failed";
      toast.error(typeof msg === "string" ? msg : "Generation failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="ai-moodboard-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-stone-700" strokeWidth={1.5} />
              Generate a moodboard with AI
            </DialogTitle>
            <DialogDescription className="text-stone-500">
              Step {step} of 3 ·{" "}
              {step === 1
                ? "Name your board"
                : step === 2
                ? "Pick the images & files"
                : "Choose a mood"}
            </DialogDescription>
          </DialogHeader>

          {step === 1 && (
            <div className="space-y-3 mt-2">
              <label className="text-xs uppercase tracking-[0.15em] text-stone-500">
                Name
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Living room, soft modern"
                data-testid="ai-mb-name-input"
                autoFocus
              />
            </div>
          )}

          {step === 2 && (
            <div className="mt-2">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-stone-500" data-testid="ai-mb-selected-count">
                  {selected.size} selected of {selectable.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={selectAll}
                    disabled={selectable.length === 0}
                    data-testid="ai-mb-select-all"
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                    disabled={selected.size === 0}
                    data-testid="ai-mb-clear"
                  >
                    Clear
                  </Button>
                </div>
              </div>
              {selectable.length === 0 ? (
                <div className="border border-dashed border-stone-200 rounded-2xl py-12 text-center text-stone-500 text-sm">
                  Nothing in the library yet. Upload images first.
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto pr-1">
                  {selectable.map((img) => {
                    const isSel = selected.has(img.id);
                    const pdf = isPdf(img);
                    const isImage = (img.mime || "").toLowerCase().startsWith("image/");
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={() => toggle(img.id)}
                        data-testid={`ai-mb-pick-${img.id}`}
                        className={`relative aspect-square rounded-lg overflow-hidden bg-stone-100 group ring-2 ${
                          isSel
                            ? "ring-stone-900 ring-offset-2 ring-offset-white"
                            : "ring-transparent"
                        } transition-all`}
                      >
                        {isImage ? (
                          <img
                            src={img.data}
                            alt={img.filename || ""}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center px-2 text-center bg-stone-100">
                            <span className="text-[10px] font-mono uppercase text-stone-500 mb-1">
                              {pdf ? "PDF" : (img.filename || "FILE").split(".").pop().slice(0, 5)}
                            </span>
                            <span className="text-[11px] text-stone-700 line-clamp-2 break-all">
                              {img.filename || categoryLabel(img.category)}
                            </span>
                          </div>
                        )}
                        {isSel && (
                          <div className="absolute top-1 right-1 w-6 h-6 rounded-full bg-stone-900 text-white flex items-center justify-center shadow">
                            <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="mt-2">
              <p className="text-sm text-stone-500 mb-3">
                Pick a vibe — it sets the background palette and the AI tagline.
              </p>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 gap-2"
                data-testid="ai-mb-mood-grid"
              >
                {MOODS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMood(m.key)}
                    data-testid={`ai-mb-mood-${m.key.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`text-left p-3 rounded-xl border transition-all ${
                      mood === m.key
                        ? "border-stone-900 shadow-sm"
                        : "border-stone-200 hover:border-stone-400"
                    }`}
                    style={{ background: m.swatch[0], color: m.swatch[1] }}
                  >
                    <p className="font-medium text-sm">{m.key}</p>
                    <p className="text-[11px] opacity-70 mt-0.5">{m.blurb}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 flex items-center justify-between gap-2">
            {step > 1 ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(step - 1)}
                disabled={busy}
                className="rounded-full"
                data-testid="ai-mb-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
                Back
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={busy}
                className="rounded-full"
              >
                Cancel
              </Button>
            )}
            {step < 3 ? (
              <Button
                type="button"
                onClick={() => {
                  if (step === 1 && !name.trim()) {
                    toast.error("Please name the board");
                    return;
                  }
                  if (step === 2 && selected.size === 0) {
                    toast.error("Pick at least one image");
                    return;
                  }
                  setStep(step + 1);
                }}
                className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                data-testid="ai-mb-next"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={busy}
                className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                data-testid="ai-mb-generate"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
                )}
                Generate
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
